/* ============================================================
   View-model selectors — ports of the prototype's extra() / reviewsData() /
   perfData(). PURE transforms over the contract (+ a little UI state): they
   compute the derived values the screens render (tone colors, percentages,
   severity mapping, filters). Interaction handlers live in the app state layer,
   NOT here. Kept faithful to design-source/…dc.html so fidelity stays 1:1.
   ============================================================ */
import type {
  Contract,
  Tone,
  Reviews,
  Perf,
  Code,
  Money,
  Site,
  ApprovalItem,
  Budget,
  HealthTile,
  ActivityEvent,
  CrewMember,
  Heartbeat,
} from './contract';

/* ---- tone helpers (verbatim from the prototype's _tone / _toneSoft / _toneLine) ---- */
export const tone = (t: Tone | string): string =>
  ({ iris: 'var(--primary)', cyan: 'var(--accent)', warn: 'var(--warn)', danger: 'var(--danger)', muted: 'var(--muted)' } as Record<string, string>)[t] || 'var(--primary)';

export const toneSoft = (t: Tone | string): string =>
  ({ iris: 'var(--primary-soft)', cyan: 'var(--accent-soft)', warn: 'var(--warn-soft)', danger: 'var(--danger-soft)', muted: 'var(--surface2)' } as Record<string, string>)[t] || 'var(--surface2)';

export const toneLine = (t: Tone | string): string =>
  ({ iris: 'var(--primary-line)', cyan: 'var(--accent-line)', warn: 'var(--warn-line)', danger: 'var(--danger-line)', muted: 'var(--border)' } as Record<string, string>)[t] || 'var(--border)';

const glowFor = (t: Tone | string): string =>
  t === 'cyan' ? 'var(--glow-cyan)' : t === 'iris' ? 'var(--glow-iris)' : 'transparent';

/* ---- Command: activity + crew-online strip ---- */
export function deriveActivity(D: Pick<Contract, 'activity'>) {
  return D.activity.map((a, i, arr) => ({ ...a, last: i === arr.length - 1, dotColor: tone(a.tone) }));
}
export function deriveCrewOnline(D: Pick<Contract, 'crew'>) {
  return D.crew.slice(0, 5).map((a) => ({ ...a, isCrew: !a.lead }));
}
export function redCount(D: Pick<Contract, 'health'>): number {
  return D.health.filter((h) => h.status === 'red').length;
}

/* ---- Machine heartbeat — the always-on box's online status ----
   Color is NEVER the only signal (WCAG 2.2 AA): every state carries a `word`
   ('online' / 'OFFLINE' / 'not connected') alongside its tone + dot. Used by
   both the Command chip and the Crew page tile. */
export interface HeartbeatView {
  agent: string;
  host: string;
  state: 'online' | 'offline' | 'never';
  online: boolean;
  word: string; // color-independent status word
  lastSeen: string; // '22s' — empty when never
  label: string; // "last seen 22s ago" / "no beat in 5m" / "no heartbeat yet"
  chip: string; // "Forge · online · 22s" — one-line for the Command chip
  tone: Tone;
  dotColor: string;
}

export function deriveHeartbeat(rows: Heartbeat[]): HeartbeatView[] {
  return (rows ?? []).map((h) => {
    const word = h.state === 'online' ? 'online' : h.state === 'offline' ? 'OFFLINE' : 'not connected';
    const toneFor: Tone = h.state === 'online' ? 'cyan' : h.state === 'offline' ? 'danger' : 'muted';
    const label =
      h.state === 'online'
        ? `last seen ${h.lastSeen} ago`
        : h.state === 'offline'
          ? `no beat in ${h.lastSeen}`
          : 'no heartbeat yet';
    const chip =
      h.state === 'never' ? `${h.agent} · not connected` : `${h.agent} · ${word} · ${h.lastSeen}`;
    return { agent: h.agent, host: h.host, state: h.state, online: h.online, word, lastSeen: h.lastSeen, label, chip, tone: toneFor, dotColor: tone(toneFor) };
  });
}

/** The lead machine (Forge) for the single Command chip — first row, or a
    synthetic "not connected" when the slice is empty. */
export function primaryHeartbeat(rows: Heartbeat[]): HeartbeatView {
  const views = deriveHeartbeat(rows);
  return views[0] ?? deriveHeartbeat([{ agent: 'Forge', host: '', online: false, state: 'never', lastSeen: '' }])[0];
}

/* ---- Crew: cards, trace, kanban, roster badges ---- */
export function deriveCrew(D: Pick<Contract, 'crew'>) {
  const costMax = Math.max(...D.crew.map((c) => c.cost));
  return D.crew.map((c) => ({
    ...c,
    isCrew: !c.lead,
    costPct: Math.round((c.cost / costMax) * 100),
    glow: c.id === 'forge',
    borderCol: c.id === 'forge' ? 'var(--accent-line)' : 'var(--border)',
    glowShadow: c.id === 'forge' ? '0 18px 50px -32px var(--glow-cyan)' : 'none',
    badgeBg: c.lead ? 'var(--primary-soft)' : 'var(--accent-soft)',
    badgeLine: c.lead ? 'var(--primary-line)' : 'var(--accent-line)',
  }));
}
export function deriveTrace(D: Pick<Contract, 'trace'>) {
  return D.trace.map((t, i, a) => ({ ...t, last: i === a.length - 1, notLast: i !== a.length - 1, dotColor: tone(t.tone) }));
}
export function deriveKanban(D: Pick<Contract, 'kanban'>) {
  return [
    { name: 'Research', tone: 'muted', items: D.kanban.Research ?? [] },
    { name: 'Design', tone: 'iris', items: D.kanban.Design ?? [] },
    { name: 'Build', tone: 'cyan', items: D.kanban.Build ?? [] },
    { name: 'Live', tone: 'cyan', items: D.kanban.Live ?? [] },
  ].map((c) => ({ ...c, count: c.items.length, accent: tone(c.tone) }));
}
export function deriveBadges(D: Pick<Contract, 'roster'>) {
  return D.roster.map((r) => ({
    ...r,
    isCrew: !r.lead,
    badgeBg: r.lead ? 'var(--primary-soft)' : 'var(--accent-soft)',
    badgeLine: r.lead ? 'var(--primary-line)' : 'var(--accent-line)',
    ink: r.lead ? 'var(--primary)' : 'var(--accent-ink)',
  }));
}

/* ---- v2 · Needs-you triage + daily brief + notifications ----
   Pure composition over already-fetched slices — no extra endpoint, so the
   card's live-ness mirrors its sources. Deep links land on the surface that
   can act on the item. */
export interface NeedsYouItem {
  id: string;
  kind: 'approval' | 'alert' | 'budget';
  text: string;
  sub: string;
  href: string;
  tone: Tone;
  glyph?: string;
}

const ALERT_HREF: Record<string, string> = {
  site: '/app/site',
  workers: '/app/site',
  ci: '/app/code',
  sentry: '/app/performance',
  anthropic: '/app/money',
};

export function deriveNeedsYou(d: {
  approvals?: ApprovalItem[];
  health?: HealthTile[];
  budget?: Budget;
}): NeedsYouItem[] {
  const items: NeedsYouItem[] = [];
  for (const h of d.health ?? []) {
    if (h.status !== 'red') continue;
    items.push({
      id: `alert-${h.id}`,
      kind: 'alert',
      text: `${h.name} is red · ${h.metric}`,
      sub: h.detail,
      href: ALERT_HREF[h.id] ?? '/app/command',
      tone: 'danger',
    });
  }
  for (const a of d.approvals ?? []) {
    if (a.status !== 'pending') continue;
    items.push({
      id: `approval-${a.id}`,
      kind: 'approval',
      text: a.title,
      sub: `${a.requestedBy} asked · ${a.time}`,
      href: '/app/controls',
      tone: a.urgency === 'high' ? 'danger' : 'warn',
      glyph: a.glyph || undefined,
    });
  }
  const pct = d.budget?.pct ?? 0;
  if (pct >= 70) {
    items.push({
      id: 'budget',
      kind: 'budget',
      text: `AI budget at ${pct}%`,
      sub: d.budget?.cycle ?? '',
      href: '/app/money',
      tone: pct >= 90 ? 'danger' : 'warn',
    });
  }
  return items;
}

export interface BriefLine {
  text: string;
  tone: Tone;
}
export function deriveDailyBrief(d: {
  health?: HealthTile[];
  budget?: Budget;
  approvals?: ApprovalItem[];
  activity?: ActivityEvent[];
  crew?: CrewMember[];
}): { headline: string; lines: BriefLine[] } {
  const lines: BriefLine[] = [];
  const health = d.health ?? [];
  const red = health.filter((h) => h.status === 'red');
  const amber = health.filter((h) => h.status === 'amber');
  if (red.length) lines.push({ text: `${red.length} system${red.length > 1 ? 's' : ''} red — ${red.map((h) => h.name).join(', ')}`, tone: 'danger' });
  else if (amber.length) lines.push({ text: `Systems mostly green · ${amber.length} amber (${amber.map((h) => h.name).join(', ')})`, tone: 'warn' });
  else if (health.length) lines.push({ text: 'All systems green', tone: 'cyan' });

  if (d.budget) {
    lines.push({
      text: `Budget at ${d.budget.pct}% · ${d.budget.unit}${d.budget.spent} of ${d.budget.unit}${d.budget.cap} · ${d.budget.daysLeft} days left`,
      tone: d.budget.pct >= 90 ? 'danger' : d.budget.pct >= 70 ? 'warn' : 'iris',
    });
  }

  const pending = (d.approvals ?? []).filter((a) => a.status === 'pending');
  if (pending.length) lines.push({ text: `${pending.length} approval${pending.length > 1 ? 's' : ''} waiting on you`, tone: 'warn' });

  const running = (d.crew ?? []).filter((c) => c.status === 'running');
  if (running.length) {
    const lead = running.find((c) => c.lead) ?? running[0];
    lines.push({ text: `Crew: ${running.length} running · ${lead.name} on “${lead.task}”`, tone: 'iris' });
  }

  const deploy = (d.activity ?? []).find((a) => a.sys === 'deploy');
  if (deploy) lines.push({ text: `Latest ship: ${deploy.text}`, tone: 'cyan' });

  const headline = red.length
    ? 'Something needs you — start with the red system.'
    : pending.length
      ? `${pending.length} decision${pending.length > 1 ? 's' : ''} waiting — otherwise steady.`
      : 'Quiet morning — the crew has it.';
  return { headline, lines };
}

export interface NotificationItem extends NeedsYouItem {
  read: boolean;
}
export function deriveNotifications(
  d: Parameters<typeof deriveNeedsYou>[0] & { activity?: ActivityEvent[] },
  readIds: string[],
): NotificationItem[] {
  const base = deriveNeedsYou(d).map((n) => ({ ...n, read: readIds.includes(n.id) }));
  const deploys = (d.activity ?? [])
    .filter((a) => a.sys === 'deploy')
    .slice(0, 2)
    .map((a, i) => ({
      id: `deploy-${a.text.slice(0, 24)}-${i}`,
      kind: 'alert' as const,
      text: a.text,
      sub: a.actor ? `${a.actor} · ${a.time}` : a.time,
      href: '/app/code',
      tone: 'iris' as Tone,
      read: readIds.includes(`deploy-${a.text.slice(0, 24)}-${i}`),
    }));
  return [...base, ...deploys];
}

/* ---- Site: top pages ---- */
export function deriveTopPages(site: Site) {
  const maxViews = Math.max(...site.topPages.map((p) => p.views));
  return site.topPages.map((p) => ({ ...p, barPct: Math.round((p.views / maxViews) * 100) }));
}

/* ---- Code: prs, ci, velocity, deploys ---- */
export function deriveCode(code: Code) {
  const ci = code.ci;
  const ciTotal = ci.passed + ci.failed + ci.running + ci.queued;
  const prs = code.prs.map((p) => ({ ...p, checkLabel: p.checks }));
  const velMax = Math.max(...code.velocity);
  const deploys = code.deploys.map((d) => ({ ...d, envColor: d.env === 'production' ? 'var(--primary)' : 'var(--muted)' }));
  return { ci, ciTotal, prs, velMax, deploys, velocity: code.velocity, velocityLabel: code.velocityLabel, oldest: code.oldest };
}

/* ---- Money: by model, totals, by agent (derived from crew cost) ---- */
export function deriveMoney(money: Money, crew: Contract['crew']) {
  const moneyTotal = money.byModel.reduce((a, b) => a + b.usd, 0);
  const infraTotal = money.infra.reduce((a, b) => a + b.usd, 0);
  const byModel = money.byModel.map((m) => ({ ...m, barColor: tone(m.tone), glow: glowFor(m.tone) }));
  // By-agent spend is the crew's per-agent cost attribution (contract §Money note).
  const agentMax = Math.max(1, ...crew.map((c) => c.cost));
  const byAgent = [...crew]
    .sort((a, b) => b.cost - a.cost)
    .map((c) => ({ id: c.id, name: c.name, glyph: c.glyph, usd: c.cost, pct: Math.round((c.cost / agentMax) * 100), lead: c.lead }));
  return { moneyTotal, infraTotal, byModel, byAgent, infra: money.infra, spend30: money.spend30 };
}

/* ---- Reviews (port of reviewsData, minus handlers) ---- */
export function deriveReviews(R: Reviews, filter: string, repliedIds: string[], apps: Contract['apps']) {
  const total = R.dist.reduce((a, b) => a + b.n, 0);
  const dist = R.dist.map((d) => ({
    stars: d.stars,
    n: d.n.toLocaleString(),
    pct: Math.round((d.n / total) * 100),
    barColor: d.stars >= 4 ? 'var(--accent)' : d.stars === 3 ? 'var(--warn)' : 'var(--danger)',
  }));
  const platforms = R.platforms.map((p) => ({ ...p, connected: p.status === 'connected', soon: p.status === 'soon' }));
  const themes = R.themes.map((t) => ({
    ...t,
    color: tone(t.tone),
    // Cyan as small text fails AA on light bg; --accent-ink is the text-safe cyan
    // (identical to --accent in dark, darker in light) so the brand is unchanged.
    inkColor: t.tone === 'cyan' ? 'var(--accent-ink)' : tone(t.tone),
    soft: toneSoft(t.tone),
    line: toneLine(t.tone),
    isIssue: t.kind === 'issue',
  }));
  const feedAll = R.feed.map((r) => {
    const replied = r.replied || repliedIds.indexOf(r.id) >= 0;
    return {
      ...r,
      replied,
      needsReply: !replied,
      filledStars: '★'.repeat(r.stars),
      emptyStars: '★'.repeat(5 - r.stars),
    };
  });
  const match = (r: (typeof feedAll)[number]) =>
    filter === 'needs' ? r.needsReply : filter === 'critical' ? r.stars <= 2 : filter === 'positive' ? r.stars >= 4 : true;
  const feed = feedAll.filter(match);
  const cnt = (id: string) =>
    id === 'all' ? feedAll.length
      : id === 'needs' ? feedAll.filter((x) => x.needsReply).length
        : id === 'critical' ? feedAll.filter((x) => x.stars <= 2).length
          : feedAll.filter((x) => x.stars >= 4).length;
  const filters = [
    { id: 'all', label: 'All' },
    { id: 'needs', label: 'Needs reply' },
    { id: 'critical', label: 'Critical' },
    { id: 'positive', label: 'Loved' },
  ].map((f) => ({
    ...f,
    active: filter === f.id,
    count: cnt(f.id),
    chipBg: filter === f.id ? 'var(--primary-soft)' : 'transparent',
    chipBorder: filter === f.id ? 'var(--primary-line)' : 'var(--border)',
    chipText: filter === f.id ? 'var(--ink)' : 'var(--muted)',
  }));
  return { app: apps[0], rv: R, dist, platforms, themes, feed, filters, empty: feed.length === 0 };
}

/* ---- Performance (port of perfData) ---- */
export function derivePerf(P: Perf) {
  const sevMap: Record<string, Tone> = { critical: 'danger', high: 'warn', medium: 'muted', low: 'muted' };
  const stMap: Record<string, { tone: Tone; pulse: boolean }> = {
    open: { tone: 'muted', pulse: false },
    investigating: { tone: 'warn', pulse: true },
    triaged: { tone: 'iris', pulse: false },
    fixed: { tone: 'cyan', pulse: false },
  };
  const issues = P.issues.map((i) => {
    const st = stMap[i.status] || stMap.open;
    return {
      ...i,
      sevColor: tone(sevMap[i.sev]),
      sevSoft: toneSoft(sevMap[i.sev]),
      sevLine: toneLine(sevMap[i.sev]),
      stColor: tone(st.tone),
      stInk: st.tone === 'cyan' ? 'var(--accent-ink)' : tone(st.tone), // text-safe cyan (see themes)
      stPulse: st.pulse,
      affected: `${i.sessions} sess · ${i.users} users`,
      hasAgent: !!i.agent,
      canAssign: i.status !== 'fixed',
      needsOwner: !i.agent && i.status !== 'fixed',
    };
  });
  const funnel = P.funnel.map((f, idx) => ({ ...f, barColor: idx === 0 ? 'var(--primary)' : 'var(--accent)' }));
  const retention = [
    { label: 'Day 1', pct: P.retention.d1 },
    { label: 'Day 7', pct: P.retention.d7 },
    { label: 'Day 30', pct: P.retention.d30 },
  ].map((r) => ({ ...r, color: 'var(--accent)' }));
  const versions = P.versions.map((v) => ({ ...v, color: tone(v.tone) }));
  return { perf: P, issues, funnel, retention, versions };
}
